/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleSystems.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 27-02-2025
 * @description : Component to add, update and replace vehicle systems.
 */
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
// import { CustomButton, CustomPagination } from "../../components";
// import CustomFiltersN from "../../components/tables/CustomFiltersN";
// import IconListManager from "../../assets/app-theme/appIcons";
import VehicleSystemsAddDialog from "../../components/labcar/VehicleSystemsAddDialog";
import VehicleSystemsEditDialog from "../../components/labcar/VehicleSystemsEditDialog";
import { HandleApiActions, labCar } from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
// import { showToastMessage } from "../../redux/actions/actionHandler";
// import actionType from "../../redux/actionTypes";
import { selectVehicleSystemAction } from "../../redux/actions/labCarActions";
// import appTheme from "../../assets/AppTheme/appTheme";
import {
  vlcCardStyles,
  vlcComponentStyle,
} from "../../components/customComponentsV2/vlc/config/vlcConfig";
import VlcCardHeader from "../../components/customComponentsV2/vlc/cards/VlcCardHeader";
import { componentStyle } from "../../config/appTheme";
import { Search } from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import VlcCardContent from "../../components/customComponentsV2/vlc/cards/VlcCardContent";
import CustomPaginationV2 from "../../components/customComponentsV2/common/pagination/CustomPaginationV2";
import CustomToolTip from "../../components/infoButton/CustomToolTip";
import { showToastMessage } from "../../redux/actions/actionHandler";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import DetailsLogTab from "../../components/customComponentsV2/vlc/pages/DetailsLogTab";

function VehicleSystems({
  getZoneVSCountList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedZoneDetails,
  variantOptionList,
  supplierOptionsList,
  vsOptionsList,
  filterState,
  onFilterChange,
  onClearFilters,
  selectedVehicleSystem,
  setSelectedVehicleSystem,
}) {
  /**
   * ===================== State Initialization =====================
   */
  /** ============== Redux ============== **/
  const dispatch = useDispatch();
  const vehicleSystems = useSelector(
    (state) => state.labCarReducer.vehicleSystems,
  );
  const selectedVehicleSystemId = useSelector(
    (state) => state.labCarReducer.selectedVehicleSystemId,
  );
  /** ============== Dialogs ============== **/
  const [openAddDialog, setOpenAddDialog] = useState(false);
  /** ============== App Icons ============== **/
  // const { AddIcon } = IconListManager();
  /** ============== Filter ============== **/
  // const [anchorElFilter, setAnchorElFilter] = useState(null);
  // const [openFilter, setOpenFilter] = useState(false);
  const [selectedSearchField, setSelectedSearchField] = useState("name");
  const [searchFieldValue, setSearchFieldValue] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);
  /** ============== Pagination ============== **/
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  // const [hasMore, setHasMore] = useState(false);

  // recreate States
  const [openRecreationVSDialog, setOpenRecreationVSDialog] = useState(false);
  const [recreationParentVSList, setRecreationParentVSList] = useState([]);
  const [selectedParentVSDetails, setSelectedParentVSDetails] = useState(null);
  const [openEditVSDialog, setOpenEditVSDialog] = useState(false);
  const [selectedVSItemForEdit, setSelectedVSItemForEdit] = useState(null);
  const [openVSChangeLogsDialog, setOpenVSChangeLogsDialog] = useState(false);
  const [vsChangeLogsList, setVSChangeLogsList] = useState([]);
  const [vsChangeLogsTotalCount, setVSChangeLogsTotalCount] = useState(0);
  const [vsLogsDialogData, setVSLogsDialogData] = useState(null);

  /**
   * ===================== Effect Hooks =====================
   */
  // Fetches vehicle systems when any of the dependencies change.
  // Dependencies: pagination, search filters, and selected project/variant detail
  const getVehicleSystems = React.useCallback(async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const isZonal = selectedProjectDetails?.is_zonal;
    const zonePosition = selectedZoneDetails?.position;
    // Call API only if required data is present
    if (!projectId || !variantId) return;
    // zonePosition can be 0, so check null/undefined explicitly
    if (isZonal && (zonePosition === null || zonePosition === undefined))
      return;
    const apiDetails = labCar.get_vehicle_systems;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          ...(isZonal && { zone_position: zonePosition }), // Add zone position if zonal
          ...(searchFieldValue && { [selectedSearchField]: searchFieldValue }), // Add search filter if available
          sort_field: filterState.sortField,
          sort_order: filterState.sortOrder,
          ...(filterState.nameFilterSearch && {
            name: filterState.nameFilterSearch,
          }),
          ...(filterState.assemblyNumFilterSearch && {
            assembly_number: filterState.assemblyNumFilterSearch,
          }),
          ...(filterState.descriptionFilterSearch && {
            description: filterState.descriptionFilterSearch,
          }),
          page_number: page,
          page_size: rowsPerPage,
        },
      }),
    );
    if (resp?.error) {
      setTotalCount(0);
      return;
    }

    setTotalCount(Number(resp?.total_count ?? 0));
    setSelectedVehicleSystem(resp?.data[0]);
  }, [
    dispatch,
    selectedProjectDetails?.project_id,
    selectedSingleVariantDetails?.variant_id,
    selectedProjectDetails?.is_zonal,
    selectedZoneDetails?.position,
    selectedSearchField,
    searchFieldValue,
    filterState.sortField,
    filterState.sortOrder,
    filterState.nameFilterSearch,
    filterState.assemblyNumFilterSearch,
    filterState.descriptionFilterSearch,
    page,
    rowsPerPage,
  ]);

  // recreate funstions

  const getParentVSDetails = async () => {
    // api call for getting parent EE components
    let parentEEResponse = await dispatch(
      HandleApiActions({
        ...labCar.get_parent_vs,
        params: {
          project_id: selectedProjectDetails.project_id,
          parent_project_id: selectedProjectDetails.parent_project_id,
          variant_id: selectedSingleVariantDetails.variant_id,
          parent_variant_id:
            selectedSingleVariantDetails?.parent_variant_details?.variant_id,
        },
      }),
    );
    if (!parentEEResponse.error) {
      setSelectedParentVSDetails(parentEEResponse.data[0] || null);
      setRecreationParentVSList(parentEEResponse.data || []);
      setOpenRecreationVSDialog(true);
    } else {
      showToastMessage({
        type: "error",
        message: parentEEResponse.msg,
      });
      setRecreationParentVSList([]);
      setSelectedParentVSDetails(null);
    }
  };

  const handleVSRecreateOpen = async () => {
    await getParentVSDetails();
  };

  const handleGetVehicleSystemChangeLogs = async ({
    pageNo = 1,
    pageSize = 25,
  } = {}) => {
    const entityId = vsLogsDialogData?.id;
    if (!entityId) return;

    const resp = await dispatch(
      HandleApiActions({
        ...labCar.vehicle_system_logs,
        params: {
          entity_id: Number(entityId),
          page_number: pageNo,
          page_size: pageSize,
        },
        show_toast: false,
      }),
    );

    if (resp?.error) {
      setVSChangeLogsList([]);
      setVSChangeLogsTotalCount(0);
      return;
    }

    setVSChangeLogsList(resp?.data || []);
    setVSChangeLogsTotalCount(resp?.total_count || 0);
  };

  const handleVSLogsDialogOpen = (item) => {
    setVSLogsDialogData(item);
    setOpenVSChangeLogsDialog(true);
  };

  const handleVSLogsDialogClose = () => {
    setOpenVSChangeLogsDialog(false);
    setVSLogsDialogData(null);
    setVSChangeLogsList([]);
    setVSChangeLogsTotalCount(0);
  };

  useEffect(() => {
    getVehicleSystems();
  }, [
    page,
    rowsPerPage,
    selectedSearchField,
    searchFieldValue,
    selectedProjectDetails,
    selectedSingleVariantDetails,
    selectedZoneDetails,
    filterState.sortField,
    filterState.sortOrder,
    filterState.nameFilter,
    filterState.assemblyNumFilter,
    filterState.descriptionFilter,
    filterState.nameFilterSearch,
    filterState.assemblyNumFilterSearch,
    filterState.descriptionFilterSearch,
    selectedZoneDetails,
    getVehicleSystems,
  ]);
  // Resets filters/patination when any of the dependencies change.
  // Dependencies: selected project and selected single variant details.
  useEffect(() => {
    // Reset filters and pagination when project or variant changes
    setSelectedSearchField("name");
    setSearchFieldValue("");
    // setRowsPerPage(25);
    setPage(1);
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  // API call handled by getVehicleSystems useCallback above

  /**
   * ===================== UI & State Management Functions =====================
   */
  /** ============== Vehicle System ============== **/
  const handleSelectVehicleSystem = (vsId, item) => {
    if (vsId) {
      dispatch(selectVehicleSystemAction(vsId));
    }
    setSelectedVehicleSystem(item);
  };
  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setActiveItemId(item?.id);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveItemId(null);
  };
  const handleMenuAction = (action, item) => {
    handleCloseMenu();
    if (action === "edit") {
      setSelectedVSItemForEdit(item);
      setOpenEditVSDialog(true);
      return;
    }
    if (action === "log") {
      handleVSLogsDialogOpen(item);
    }
  };

  /** ============== Pagination ============== **/
  // Updates the current page
  const handlePageChange = (value) => setPage(value);

  const [nameSearch, setNameSearch] = useState("");
  const debounceRef = useRef();

  useEffect(() => {
    // Always clear any existing debounce timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Only trigger debounce if 3+ chars OR 0 (cleared)
    if (nameSearch.length >= 3 || nameSearch.length === 0) {
      debounceRef.current = setTimeout(() => {
        setSelectedSearchField("name");
        setSearchFieldValue(nameSearch); // Triggers API
        setPage(1);
      }, 500);
    }

    // Cleanup
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [nameSearch]);

  return (
    <Box>
      {/* Vehicle systems card */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Card // sx={styles.eeCompCard}
          sx={{
            ...vlcComponentStyle.card,
            display: "flex",
            flexDirection: "column",
            height: "450px",
          }}
        >
          <CardHeader
            sx={vlcComponentStyle.cardHeader}
            title={
              <VlcCardHeader
                title={"Vehicle Systems"}
                showRecreateIcon={
                  selectedProjectDetails?.parent_project_id &&
                    selectedSingleVariantDetails?.parent_variant_details
                      ?.variant_id
                    ? true
                    : false
                }
                onRecreateClick={handleVSRecreateOpen}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  sx={{
                    width: "100%",
                    mt: 1,
                  }}
                >

                  <TextField
                    size="small"
                    placeholder="Search Name"
                    variant="outlined"
                    fullWidth
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),

                    }}
                    sx={componentStyle.searchField.textField}
                  />
                  <Box>
                    <AddButtonV2
                      handleOnClick={(e) => setOpenAddDialog(true)}
                      disabled={!selectedSingleVariantDetails?.variant_id}
                    />
                  </Box>
                </Box>
              </VlcCardHeader>
            }
          />
          <CardContent
            sx={{
              ...vlcCardStyles.CardContent,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 0,
              scrollbarColor: "#888 #f1f1f1",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
              // backgroundColor: "#0000ff",
            }}
          >
            {selectedSingleVariantDetails?.variant_id &&
              vehicleSystems &&
              vehicleSystems?.length > 0 ? (
              vehicleSystems?.map((item, index) => (
                <VlcCardContent
                  key={item.id}
                  onClick={() => handleSelectVehicleSystem(item?.id, item)}
                  data={item}
                  isSelected={selectedVehicleSystemId === item?.id}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        p: 1,
                        width: "90%",
                      }}
                    >
                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={50}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                            fontWeight: "bold",
                          }}
                          title={`Name: ${item?.name ?? ""}`}
                        >
                          {`Name: ${item?.name ?? ""}`}
                        </CustomToolTip>
                      </Typography>
                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          color: vlcCardStyles.vlcCardListColor,
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={60}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                          title={`Assembly No: ${item?.assembly_number ?? ""}`}
                        >
                          {`Assembly No: ${item?.assembly_number ?? ""}`}
                        </CustomToolTip>
                      </Typography>
                      <Typography
                        sx={{
                          ...componentStyle.font.text,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                          color: vlcCardStyles.vlcCardListColor,
                        }}
                      >
                        <CustomToolTip
                          type="string"
                          length={60}
                          style={{
                            ...componentStyle.font.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "100%",
                          }}
                          title={`Description: ${item?.description ?? ""}`}
                        >
                          {`Description: ${item?.description ?? ""}`}
                        </CustomToolTip>
                      </Typography>
                    </Box>
                    <Box sx={{ pr: 1, pt: 1 }}>
                      <IconButton
                        size="small"
                        sx={{ color: "black" }}
                        onClick={(e) => handleMoreClick(e, item)}
                      >
                        <MoreVertIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && activeItemId === item?.id}
                        onClose={handleCloseMenu}
                        PaperProps={{
                          sx: {
                            borderRadius: 0,
                            boxShadow: 1,
                          },
                        }}
                      >
                        <MenuItem
                          onClick={() => handleMenuAction("edit", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          Edit details
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleMenuAction("log", item)}
                          sx={{ ...componentStyle.font.cardText }}
                        >
                          Log
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                </VlcCardContent>
              ))
            ) : (
              <Typography variant="body1" sx={styles.noDataAvailable}>
                No data available
              </Typography>
            )}
          </CardContent>
          {/* ============== Pagination ============== */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              // p: { xs: "2px", sm: "3px" },
              minHeight: "40px",
              borderTop: "1px solid #ddd",
              borderRadius: "0px !important",
            }}
          >

            {(totalCount || vehicleSystems?.length || 0) > 0 && (
              <CustomPaginationV2
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                totalItems={Math.max(
                  totalCount || 0,
                  vehicleSystems?.length || 0,
                )}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* ============== Dialogs ============== */}
      {/* Add vehicle system dialog */}
      <VehicleSystemsAddDialog
        openDialog={openAddDialog}
        setOpenDialog={setOpenAddDialog}
        title={"Add Vehicle System"}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        selectedZoneDetails={selectedZoneDetails}
        supplierOptionsList={supplierOptionsList}
        vsOptionsList={vsOptionsList}
        getVehicleSystems={getVehicleSystems}
        getZoneVSCountList={getZoneVSCountList}
      />

      {/* recreate dialog */}
      <VehicleSystemsAddDialog
        openDialog={openRecreationVSDialog}
        setOpenDialog={setOpenRecreationVSDialog}
        title={"Recreate Vehicle System"}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        selectedZoneDetails={selectedZoneDetails}
        supplierOptionsList={supplierOptionsList}
        vsOptionsList={vsOptionsList}
        getVehicleSystems={getVehicleSystems}
        getZoneVSCountList={getZoneVSCountList}
        recreationMode={true}
        recreationParentVSList={recreationParentVSList}
        selectedParentVSDetails={selectedParentVSDetails}
        setSelectedParentVSDetails={setSelectedParentVSDetails}
      />
      <VehicleSystemsEditDialog
        openDialog={openEditVSDialog}
        setOpenDialog={setOpenEditVSDialog}
        selectedVSItem={selectedVSItemForEdit}
        variantOptionList={variantOptionList}
        selectedProjectDetails={selectedProjectDetails}
        selectedSingleVariantDetails={selectedSingleVariantDetails}
        supplierOptionsList={supplierOptionsList}
        vsOptionsList={vsOptionsList}
        getVehicleSystems={getVehicleSystems}
        getZoneVSCountList={getZoneVSCountList}
      />

      <CustomDialogBoxV2
        open={openVSChangeLogsDialog}
        onClose={handleVSLogsDialogClose}
        title={"Vehicle Systems"}
      >
        <Box sx={{ width: "450px", height: "500px" }}>
          <DetailsLogTab
            logData={vsLogsDialogData}
            ChangeLogsList={vsChangeLogsList}
            getLogsData={handleGetVehicleSystemChangeLogs}
            selectedComponent={vsLogsDialogData}
            zone_name={vsLogsDialogData?.zone_details?.zone_name}
            variantOptionList={variantOptionList}
            selectedSingleVariantDetails={selectedSingleVariantDetails}
            openEditDetailsDialog={openVSChangeLogsDialog}
            setOpenEditDetailsDialog={setOpenVSChangeLogsDialog}
            totalLogsCount={vsChangeLogsTotalCount}
            type={"VS"}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

export default VehicleSystems;

// Styles
const styles = {
  vsContainer: {
    flex: 1,
    minWidth: 0,
    minHeight: { xs: "350px", sm: "400px", md: "500px" },
    maxHeight: { xs: "500px", sm: "600px", md: "700px" },
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "stretch",
    height: "100%",
    boxSizing: "border-box",
  },
  card: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "100%",
    boxShadow: 4,
    borderRadius: "3%",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    py: { xs: 0.3, sm: 0.5 },
    backgroundColor: "lightgrey",
    borderBottom: "1px solid lightgrey",
    borderRadius: "2%",
  },
  cardContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarColor: "#888 #f1f1f1",
    scrollbarWidth: "thin",
    px: { xs: 0.5, sm: 1 },
    py: { xs: 0.5, sm: 1 },
  },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    p: { xs: "2px", sm: "3px" },
  },
  noDataAvailable: {
    textAlign: "center",
    mt: { xs: 1, sm: 2 },
    fontSize: { xs: "0.9rem", sm: "1rem" },
  },
  cardContentCards: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 0.5, sm: 1 },
    pl: { xs: 0.5, sm: 1 },
    pr: { xs: 0.2, sm: 0.5 },
    backgroundColor: "background.paper",
    borderRadius: 2,
    boxShadow: 1,
    cursor: "pointer",
    minHeight: { xs: 44, sm: 54 },
    maxWidth: "100%",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  cardContentCardContainer: {
    textAlign: "start",
    my: { xs: 0.5, sm: 1 },
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    gap: 0.5,
  },
};
